import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import App from './App';
import { EmbedApp } from './embed/EmbedApp';
import { EmbeddedEditorApp, parentPoster } from './embed/proposal-editor/EmbeddedEditorApp';
import { EMBEDDED_EDITOR_PREFIX, installEditorSession, parseFrameAncestors } from './embed/proposal-editor/session';
import { getRuntimeConfig } from './lib/runtime-config';
import { installEmbedAuth } from './embed/auth';
import { PrintPage } from './proposals/PrintPage';
import { AdminAuthProvider } from './components/AdminAuthProvider';
import { queryClient } from './lib/query-client';
import { installSandboxIfActive } from './sandbox/mode';
import { consumeE2ETokenFromLocation, getE2EToken } from './lib/e2e-session';
import { setTokenProvider } from './lib/api';
import './index.css';

// The sandbox (tours) swaps the API for an in-browser store; it has to be
// in place before the first query.
installSandboxIfActive();
// TEST-ONLY admin sign-in: a no-op unless this dev server runs with VITE_E2E_AUTH=1.
consumeE2ETokenFromLocation();

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root is missing from index.html');
}

/**
 * The Salesforce Canvas embed is a different surface: it runs inside a
 * cross-site iframe, authenticates by the bearer token the `/canvas` SSO handler
 * passed in the URL fragment (a cookie is set too, but the browser refuses to
 * send it back inside the frame), and must NOT mount the admin Clerk gate (there
 * is no staff Clerk session in the iframe, and the sign-in screen cannot work
 * there). So `/embed` boots a minimal tree — React Query for data, no Clerk, no
 * AdminShell — after registering that token.
 */
// The proposal editor embedded in Salesforce's Edit modal (/embed/proposals/:id)
// is its own surface: it runs on a scoped Editor token from the URL fragment,
// held in memory and sent as `Authorization: Editor …` — never the Canvas
// bearer token, never a Clerk session.
const isEmbeddedEditor = window.location.pathname.startsWith(EMBEDDED_EDITOR_PREFIX);
const editorSession = isEmbeddedEditor ? installEditorSession() : null;
const isEmbed = !isEmbeddedEditor && window.location.pathname.startsWith('/embed');
// The proposal print view (/print/:id) is a bare, unscoped render of the
// document — no admin shell, no Clerk gate. A person's same-origin session
// cookie authenticates its data fetch; the PDF render service (headless
// Chromium) arrives instead with a scoped print token in the URL fragment,
// registered the same way the Salesforce embed registers its token.
const isPrint = window.location.pathname.startsWith('/print/');
// TEST-ONLY: a test sign-in has no Clerk cookie, so the print view sends the
// test token instead. A print token in the fragment (installed next) still wins.
const testToken = isPrint ? getE2EToken() : null;
if (testToken) setTokenProvider(() => Promise.resolve(testToken));
if (isEmbed || isPrint) {
  installEmbedAuth();
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {isEmbeddedEditor ? (
        <EmbeddedEditorApp
          session={editorSession}
          postToParent={parentPoster(window, parseFrameAncestors(getRuntimeConfig('EMBED_FRAME_ANCESTORS')))}
        />
      ) : isEmbed ? (
        <EmbedApp />
      ) : isPrint ? (
        <PrintPage />
      ) : (
        <BrowserRouter>
          <AdminAuthProvider>
            <App />
            {/* One toaster for the whole app: short confirmations that clear on their own. */}
            <Toaster position="bottom-right" closeButton />
          </AdminAuthProvider>
        </BrowserRouter>
      )}
    </QueryClientProvider>
  </StrictMode>,
);
